// utils/formVersionResolver.js
// -----------------------------------------------------------------------------
// Purpose
//   Centralized “version selection” for form blobs (layout/theme/metadata/etc.)
//   across the three states your schema supports:
//
//     1) Published root blobs  (forms/{formId}.layoutConfig, theme, fieldsMetadata, ...)
//     2) Draft root blobs      (forms/{formId}.status === 'draft' and blobs on root)
//     3) Draft overlay blobs   (forms/{formId}.draftOverlay.{layoutConfig, theme, ...}
//                              where draftOverlay.status === 'draftOverlay'
//                              OR forms/{formId}.draftOverlayStatus === 'draftOverlay')
//
// Notes
//   • This file is intentionally agnostic to compression/decompression.
//     It returns the *stored value* (Base64 string or object) and the chosen source.
//   • Your contexts can decompress as they already do.
//   • This file does NOT read from session/local storage. It only resolves Firestore doc shapes.
//     (You can keep storage as an additional upstream layer if desired.)
//
// “Version mode”
//   mode:
//     - 'auto'      => overlay if active & present; else root blobs
//     - 'overlay'   => overlay only (error/fallback behavior configurable)
//     - 'root'      => root only (published or draft root)
//     - 'published' => root only, but only if root status is published (best-effort)
//     - 'draft'     => prefer overlay when published; else root draft when root status is draft
//
// You can add UI on top of this later (user chooses “Published” vs “Saved draft”).
// For now, 'auto' matches your current getLayoutConfig behavior.
// -----------------------------------------------------------------------------

/**
 * Lightweight type guards
 */
const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;

/**
 * Builder UI view-mode (what the user picks) -> resolver mode (what we execute).
 *
 * UI-facing values we want to support:
 *   - 'auto'      => overlay if present, else root (current default)
 *   - 'draft'     => prefer working copy (overlay when root is published; else root draft)
 *   - 'published' => force published root blobs (ignore overlay)
 *
 * NOTE:
 *   This does NOT read from storage. Storage/route/UI decides the viewMode
 *   and passes it into the resolver via opts.viewMode (or opts.mode directly).
 */
export const normalizeBuilderViewMode = (viewMode) => {
  const v = String(viewMode || 'auto').trim().toLowerCase();

  if (v === 'published' || v === 'publish') return 'published';

  // Treat these as “working copy / overlay”
  // Supports your FormLayoutContext value: 'draftOverlay'
  // NOTE: keep 'draft' distinct so resolver can run the 'draft' policy when requested.
  if (v === 'draft') {
    return 'draft';
  }

  if (
    v === 'overlay' ||
    v === 'draftoverlay' ||
    v === 'draft_overlay' ||
    v === 'draft-overlay'
  ) {
    return 'draftOverlay';
  }


  if (v === 'root') return 'published'; // treat as published for UI intents
  if (v === 'auto') return 'auto';

  // default safe behavior
  return 'auto';
};


export const mapViewModeToResolverMode = (viewMode) => {
  const v = normalizeBuilderViewMode(viewMode);

  // Map the UI selection to the resolver’s mode switch
  if (v === 'published') return 'published';

  // Force overlay-only (draftOverlay.*) when user picks “Saved draft”
  if (v === 'draftOverlay') return 'overlay';

  // “Draft” behavior (prefer overlay when published; else root draft)
  if (v === 'draft') return 'draft';

  return 'auto';
};

/**
 * Returns a normalized snapshot of the doc’s “version state”.
 * This helps logging and debugging.
 */
export const getFormVersionSnapshot = (formData) => {
  const d = isObject(formData) ? formData : {};

  const rootStatus = d?.status || 'none';
  const overlay = isObject(d?.draftOverlay) ? d.draftOverlay : null;

  const overlayStatus = overlay?.status || d?.draftOverlayStatus || null;

  const overlayActive =
    overlayStatus === 'draftOverlay' ||
    d?.draftOverlayStatus === 'draftOverlay';

  const overlaySavedAt =
    overlay?.savedAt ||
    d?.draftOverlaySavedAt ||
    null;

  const overlaySavedBy =
    overlay?.savedBy ||
    d?.draftOverlaySavedBy ||
    null;

  return {
    rootStatus,
    overlayActive,
    overlayStatus,
    overlaySavedAt,
    overlaySavedBy,
    hasOverlayObject: !!overlay,
  };
};

/**
 * Determines whether overlay is “active enough” to be considered.
 * We allow either:
 *   - overlay.status === 'draftOverlay'
 *   - OR root draftOverlayStatus === 'draftOverlay'
 * because your code writes both (overlay.status + root-level index fields).
 */
export const isDraftOverlayActive = (formData) => {
  const snap = getFormVersionSnapshot(formData);
  return snap.overlayActive;
};

/**
 * Safe getter for a Firestore doc (or doc-like object).
 * Supports nested paths as an array: ['draftOverlay','layoutConfig'].
 */
const getPath = (obj, pathArr) => {
  let cur = obj;
  for (const key of pathArr) {
    if (!isObject(cur) || !(key in cur)) return undefined;
    cur = cur[key];
  }
  return cur;
};

/**
 * Decide which source to use for a given blobKey.
 *
 * @param {object} formData - Firestore forms/{formId} doc data.
 * @param {string} blobKey  - One of: 'compressedData','layoutConfig','theme','fieldsMetadata',
 *                            'dropdownOptions','dropdownDependencies','submissionSettings'
 *                            (or any key that exists in your schema)
 * @param {object} opts
 * @param {'auto'|'overlay'|'root'|'published'|'draft'} opts.mode
 * @param {boolean} opts.allowEmpty - if true, return empty string/object values as valid
 * @param {boolean} opts.fallbackToRoot - if true, overlay-only mode can fall back to root
 * @param {boolean} opts.debug - if true, returns extra debugging info
 *
 * @returns {object} { value, source, path, version, snapshot, debug? }
 *   source: 'draftOverlay' | 'root'
 *   version: 'overlay' | 'published' | 'draft' | 'unknown'
 *   path: string path like 'draftOverlay.layoutConfig' or 'layoutConfig'
 */
export const resolveBlobSource = (
  formData,
  blobKey,
  opts = {}
) => {
  const {
    // You can pass either:
    //   opts.mode     -> resolver mode ('auto'|'overlay'|'root'|'published'|'draft')
    //   opts.viewMode -> UI mode ('auto'|'draft'|'published')
    mode: modeRaw,
    viewMode, // UI-facing alias
    allowEmpty = false,
    fallbackToRoot = true,
    debug = false,
  } = opts;

  // If caller provided viewMode, it takes precedence unless they explicitly set mode
  const mode = (modeRaw != null)
    ? modeRaw
    : mapViewModeToResolverMode(viewMode);

  const d = isObject(formData) ? formData : {};
  const snapshot = getFormVersionSnapshot(d);

  const overlayActive = snapshot.overlayActive;
  const overlay = isObject(d?.draftOverlay) ? d.draftOverlay : null;

  // Candidate values
  const overlayPath = ['draftOverlay', blobKey];
  const rootPath = [blobKey];

  const overlayVal = overlay ? getPath(d, overlayPath) : undefined;
  const rootVal = getPath(d, rootPath);

  // Value “presence” check:
  // - If allowEmpty=false, treat '' as missing; treat {} as present (object could be empty intentionally)
  const isPresent = (v) => {
    if (v === undefined || v === null) return false;
    if (!allowEmpty && typeof v === 'string' && v.length === 0) return false;
    return true;
  };

  const overlayPresent = overlayActive && isPresent(overlayVal);
  const rootPresent = isPresent(rootVal);

  // Derive a human-friendly version label for root
  const rootVersion =
    snapshot.rootStatus === 'published'
      ? 'published'
      : snapshot.rootStatus === 'draft'
        ? 'draft'
        : 'unknown';

  // Mode routing -------------------------------------------------------------
  // 1) overlay-only
  if (mode === 'overlay') {
    if (overlayPresent) {
      return {
        value: overlayVal,
        source: 'draftOverlay',
        path: `draftOverlay.${blobKey}`,
        version: 'overlay',
        snapshot,
        ...(debug ? { debug: { overlayPresent, rootPresent } } : {}),
      };
    }

    if (fallbackToRoot && rootPresent) {
      return {
        value: rootVal,
        source: 'root',
        path: blobKey,
        version: rootVersion,
        snapshot,
        ...(debug ? { debug: { overlayPresent, rootPresent, fallbackToRoot } } : {}),
      };
    }

    return {
      value: null,
      source: overlayActive ? 'draftOverlay' : 'draftOverlay',
      path: `draftOverlay.${blobKey}`,
      version: 'overlay',
      snapshot,
      ...(debug ? { debug: { overlayPresent, rootPresent, fallbackToRoot } } : {}),
    };
  }

  // 2) root-only
  if (mode === 'root') {
    return {
      value: rootPresent ? rootVal : null,
      source: 'root',
      path: blobKey,
      version: rootVersion,
      snapshot,
      ...(debug ? { debug: { rootPresent } } : {}),
    };
  }

  // 3) published-only (best-effort)
  if (mode === 'published') {
    const ok = snapshot.rootStatus === 'published';
    return {
      value: ok && rootPresent ? rootVal : null,
      source: 'root',
      path: blobKey,
      version: ok ? 'published' : rootVersion,
      snapshot,
      ...(debug ? { debug: { rootPresent, ok } } : {}),
    };
  }

  // 4) draft-only (prefer overlay when published, else root draft)
  if (mode === 'draft') {
    // If published + overlay exists, use overlay; otherwise use root draft blobs.
    if (snapshot.rootStatus === 'published' && overlayPresent) {
      return {
        value: overlayVal,
        source: 'draftOverlay',
        path: `draftOverlay.${blobKey}`,
        version: 'overlay',
        snapshot,
        ...(debug ? { debug: { overlayPresent, rootPresent } } : {}),
      };
    }

    // If root is draft, root blobs are the draft
    if (snapshot.rootStatus === 'draft' && rootPresent) {
      return {
        value: rootVal,
        source: 'root',
        path: blobKey,
        version: 'draft',
        snapshot,
        ...(debug ? { debug: { overlayPresent, rootPresent } } : {}),
      };
    }

    // Fallbacks (still return something deterministic)
    if (overlayPresent) {
      return {
        value: overlayVal,
        source: 'draftOverlay',
        path: `draftOverlay.${blobKey}`,
        version: 'overlay',
        snapshot,
        ...(debug ? { debug: { overlayPresent, rootPresent } } : {}),
      };
    }

    return {
      value: rootPresent ? rootVal : null,
      source: 'root',
      path: blobKey,
      version: rootVersion,
      snapshot,
      ...(debug ? { debug: { overlayPresent, rootPresent } } : {}),
    };
  }

  // 5) auto (default): overlay if active & has this blob; else root
  if (overlayPresent) {
    return {
      value: overlayVal,
      source: 'draftOverlay',
      path: `draftOverlay.${blobKey}`,
      version: 'overlay',
      snapshot,
      ...(debug ? { debug: { overlayPresent, rootPresent } } : {}),
    };
  }

  return {
    value: rootPresent ? rootVal : null,
    source: 'root',
    path: blobKey,
    version: rootVersion,
    snapshot,
    ...(debug ? { debug: { overlayPresent, rootPresent } } : {}),
  };
};

/**
 * Convenience wrappers for the specific blobs you already store.
 * (These just call resolveBlobSource with the correct key.)
 */
export const resolveCompressedData = (formData, opts = {}) =>
  resolveBlobSource(formData, 'compressedData', opts);

export const resolveLayoutConfig = (formData, opts = {}) =>
  resolveBlobSource(formData, 'layoutConfig', opts);

export const resolveThemeBlob = (formData, opts = {}) =>
  resolveBlobSource(formData, 'theme', opts);

export const resolveFieldsMetadata = (formData, opts = {}) =>
  resolveBlobSource(formData, 'fieldsMetadata', opts);

export const resolveDropdownOptions = (formData, opts = {}) =>
  resolveBlobSource(formData, 'dropdownOptions', opts);

export const resolveDropdownDependencies = (formData, opts = {}) =>
  resolveBlobSource(formData, 'dropdownDependencies', opts);

export const resolveSubmissionSettings = (formData, opts = {}) =>
  resolveBlobSource(formData, 'submissionSettings', opts);

/**
 * Optional: Resolve all common blobs at once (useful for debugging).
 * Returns an object keyed by blob name, each containing the resolved result.
 */
export const resolveAllFormBlobs = (formData, opts = {}) => {
  return {
    compressedData: resolveCompressedData(formData, opts),
    layoutConfig: resolveLayoutConfig(formData, opts),
    theme: resolveThemeBlob(formData, opts),
    fieldsMetadata: resolveFieldsMetadata(formData, opts),
    dropdownOptions: resolveDropdownOptions(formData, opts),
    dropdownDependencies: resolveDropdownDependencies(formData, opts),
    submissionSettings: resolveSubmissionSettings(formData, opts),
  };
};

/**
 * Optional: Resolve metadata fields with an overlay-aware policy.
 * This is NOT required for layout testing, but it’s here so you don’t have to revisit.
 *
 * Example usage:
 *   resolveMetaField(formData, 'title', { mode:'auto' })
 *
 * Behavior:
 *   - If overlayActive and draftOverlay has that field, it can be used (mode-dependent).
 *   - Otherwise falls back to root.
 *
 * This is intentionally conservative; you can refine per-field later.
 */
export const resolveMetaField = (formData, metaKey, opts = {}) => {
  const {
    mode = 'auto',
    allowEmpty = false,
    fallbackToRoot = true,
    debug = false,
  } = opts;

  const d = isObject(formData) ? formData : {};
  const snapshot = getFormVersionSnapshot(d);

  const overlayActive = snapshot.overlayActive;
  const overlay = isObject(d?.draftOverlay) ? d.draftOverlay : null;

  const overlayVal = overlay ? overlay?.[metaKey] : undefined;
  const rootVal = d?.[metaKey];

  const isPresent = (v) => {
    if (v === undefined || v === null) return false;
    if (!allowEmpty && typeof v === 'string' && v.length === 0) return false;
    return true;
  };

  const overlayPresent = overlayActive && isPresent(overlayVal);
  const rootPresent = isPresent(rootVal);

  // modes mirror blob behavior
  if (mode === 'overlay') {
    if (overlayPresent) {
      return {
        value: overlayVal,
        source: 'draftOverlay',
        path: `draftOverlay.${metaKey}`,
        version: 'overlay',
        snapshot,
        ...(debug ? { debug: { overlayPresent, rootPresent } } : {}),
      };
    }
    if (fallbackToRoot && rootPresent) {
      return {
        value: rootVal,
        source: 'root',
        path: metaKey,
        version: snapshot.rootStatus === 'published' ? 'published' : 'draft',
        snapshot,
        ...(debug ? { debug: { overlayPresent, rootPresent } } : {}),
      };
    }
    return {
      value: null,
      source: 'draftOverlay',
      path: `draftOverlay.${metaKey}`,
      version: 'overlay',
      snapshot,
      ...(debug ? { debug: { overlayPresent, rootPresent } } : {}),
    };
  }

  if (mode === 'root' || mode === 'published' || mode === 'draft') {
    // for meta fields, we keep it simple: root-only is safest unless you explicitly ask overlay
    return {
      value: rootPresent ? rootVal : null,
      source: 'root',
      path: metaKey,
      version: snapshot.rootStatus === 'published' ? 'published' : 'draft',
      snapshot,
      ...(debug ? { debug: { rootPresent } } : {}),
    };
  }

  // auto
  if (overlayPresent) {
    return {
      value: overlayVal,
      source: 'draftOverlay',
      path: `draftOverlay.${metaKey}`,
      version: 'overlay',
      snapshot,
      ...(debug ? { debug: { overlayPresent, rootPresent } } : {}),
    };
  }

  return {
    value: rootPresent ? rootVal : null,
    source: 'root',
    path: metaKey,
    version: snapshot.rootStatus === 'published' ? 'published' : 'draft',
    snapshot,
    ...(debug ? { debug: { overlayPresent, rootPresent } } : {}),
  };
};
